import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  FormActionButtons,
  FormField,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@seller/app/locale";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import type { CouponView } from "@/lib/api/types";
import { useCreateCouponMutation } from "@/modules/coupons/services/useCreateCouponMutation";
import { useUpdateCouponMutation } from "@/modules/coupons/services/useUpdateCouponMutation";
import { PromotionFields } from "@/modules/coupons/components/PromotionFields";
import {
  buildPromotionPayloadFields,
  discountPromotionKeys,
  initialPromotionState,
  validatePromotionState,
  type PromotionFieldErrors,
  type PromotionState,
} from "@/modules/coupons/lib/promotion-form";

type Mode = "create" | "edit";

type DiscountFormProps = {
  storeSlug: string;
  mode: Mode;
  discount?: CouponView;
  onCancel: () => void;
  onSuccess: () => void;
  /**
   * When provided, the form renders without inline Cancel/Save buttons and
   * exposes its `id` so an external Save button (e.g. inside a Modal footer)
   * can submit it via the native `form="<id>"` attribute.
   */
  formId?: string;
  /** Called whenever the submit-in-flight state flips. Used by the parent to
   * disable the external footer button. */
  onSubmittingChange?: (busy: boolean) => void;
};

type FieldErrors = {
  minOrderAmount?: string;
  schedule?: string;
};

/** ISO string → `datetime-local` input value (YYYY-MM-DDTHH:mm) in local time. */
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export function DiscountForm({
  storeSlug,
  mode,
  discount,
  onCancel,
  onSuccess,
  formId,
  onSubmittingChange,
}: DiscountFormProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [promo, setPromo] = useState<PromotionState>(() =>
    initialPromotionState(discount),
  );
  const [promoErrors, setPromoErrors] = useState<PromotionFieldErrors>({});
  const [minOrderAmount, setMinOrderAmount] = useState<string>(
    discount?.minOrderAmount !== null && discount?.minOrderAmount !== undefined
      ? String(discount.minOrderAmount)
      : "",
  );
  const [startsAt, setStartsAt] = useState<string>(
    discount?.startsAt ? isoToDatetimeLocal(discount.startsAt) : "",
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    discount?.expiresAt ? isoToDatetimeLocal(discount.expiresAt) : "",
  );
  const [combinable, setCombinable] = useState(discount?.combinable ?? false);
  const [active, setActive] = useState(discount?.active ?? true);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync: createCoupon, isPending: isCreating } =
    useCreateCouponMutation();
  const { mutateAsync: updateCoupon, isPending: isUpdating } =
    useUpdateCouponMutation();
  const isSubmitting = isCreating || isUpdating;

  const updatePromo = (patch: Partial<PromotionState>) =>
    setPromo((prev) => ({ ...prev, ...patch }));
  const clearPromoError = (field: keyof PromotionFieldErrors) =>
    setPromoErrors((prev) => ({ ...prev, [field]: undefined }));

  const validate = (): FieldErrors | null => {
    const errors: FieldErrors = {};

    if (minOrderAmount.trim().length > 0) {
      const numericMinOrder = Number(minOrderAmount);
      if (Number.isNaN(numericMinOrder) || numericMinOrder <= 0) {
        errors.minOrderAmount = t(keys.discounts.errorMinOrderInvalid);
      }
    }

    if (startsAt.trim().length > 0 && expiresAt.trim().length > 0) {
      if (new Date(startsAt).getTime() >= new Date(expiresAt).getTime()) {
        errors.schedule = t(keys.discounts.errorScheduleOrder);
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const previewText = useMemo(() => {
    const { kind, discountUnit, discountValue } = promo;
    if (kind === "FREE_DELIVERY") {
      return t(keys.discounts.previewFreeDelivery);
    }
    if (kind === "BUY_X_GET_Y") {
      const buy = Number(promo.buyQuantity);
      const get = Number(promo.getQuantity);
      const percent = Number(promo.getDiscountPercent);
      const ready =
        promo.buyQuantity.trim().length > 0 &&
        promo.getQuantity.trim().length > 0 &&
        promo.getDiscountPercent.trim().length > 0 &&
        !Number.isNaN(buy) &&
        !Number.isNaN(get) &&
        !Number.isNaN(percent);
      if (!ready) return t(keys.discounts.previewBogoUnset);
      const clampedPercent = Math.min(Math.max(percent, 0), 100);
      return clampedPercent >= 100
        ? t(keys.discounts.previewBogoFree, { buy, get })
        : t(keys.discounts.previewBogoPercent, {
            buy,
            get,
            percent: clampedPercent,
          });
    }
    // CART_DISCOUNT + PRODUCT_DISCOUNT
    const isProduct = kind === "PRODUCT_DISCOUNT";
    const numericValue = Number(discountValue);
    const hasValue =
      discountValue.trim().length > 0 &&
      !Number.isNaN(numericValue) &&
      numericValue > 0;
    if (!hasValue) {
      return isProduct
        ? t(keys.discounts.previewProductDiscountUnset)
        : t(keys.discounts.previewCartDiscountUnset);
    }
    if (discountUnit === "PERCENT") {
      const clamped = Math.min(numericValue, 100);
      return isProduct
        ? t(keys.discounts.previewProductDiscountPercent, { percent: clamped })
        : t(keys.discounts.previewCartDiscountPercent, { percent: clamped });
    }
    const amount = formatCurrency(numericValue, locale);
    return isProduct
      ? t(keys.discounts.previewProductDiscountFixed, { amount })
      : t(keys.discounts.previewCartDiscountFixed, { amount });
  }, [promo, locale, t]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    const errors = validate();
    const pErrors = validatePromotionState(promo, t, discountPromotionKeys);
    if (errors || pErrors) {
      setFieldErrors(errors ?? {});
      setPromoErrors(pErrors ?? {});
      return;
    }
    setFieldErrors({});
    setPromoErrors({});

    const parsedMinOrder =
      minOrderAmount.trim().length > 0 ? Number(minOrderAmount) : null;
    const parsedStartsAt =
      startsAt.trim().length > 0 ? new Date(startsAt).toISOString() : null;
    const parsedExpiresAt =
      expiresAt.trim().length > 0 ? new Date(expiresAt).toISOString() : null;
    const promotionFields = buildPromotionPayloadFields(promo);

    try {
      if (mode === "create") {
        await createCoupon({
          storeSlug,
          trigger: "AUTOMATIC",
          ...promotionFields,
          combinable,
          active,
          minOrderAmount: parsedMinOrder,
          startsAt: parsedStartsAt,
          expiresAt: parsedExpiresAt,
        });
      } else if (discount) {
        await updateCoupon({
          storeSlug,
          couponId: discount.id,
          // `trigger` is set at creation and immutable — the update DTO
          // rejects it (forbidNonWhitelisted), so it must not be sent here.
          ...promotionFields,
          combinable,
          active,
          minOrderAmount: parsedMinOrder,
          startsAt: parsedStartsAt,
          expiresAt: parsedExpiresAt,
        });
      }
      onSuccess();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  // Surface the submit state to the parent so an external footer Save button
  // (rendered in the Modal footer) can disable while a mutation is in flight.
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-5">
      {submitError ? (
        <Alert message={submitError} helperText={t(keys.errors.recovery)} />
      ) : null}

      {/* Live preview banner */}
      <div
        className="flex items-start gap-3 rounded-xl border border-[color:var(--accent-500)]/20 bg-gradient-to-br from-[color:var(--accent-500)]/5 to-transparent px-4 py-3"
        aria-live="polite"
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--accent-500)]/15 text-[color:var(--accent-600)]"
          aria-hidden="true"
        >
          {promo.kind === "FREE_DELIVERY" ? <TruckIcon /> : <PercentIcon />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
            {t(keys.discounts.previewLabel)}
          </span>
          <span className="text-[13.5px] leading-snug text-[color:var(--text-primary)]">
            {previewText}
          </span>
        </div>
      </div>

      {/* Kind + value / BOGO / scope */}
      <PromotionFields
        k={discountPromotionKeys}
        storeSlug={storeSlug}
        locale={locale}
        value={promo}
        onChange={updatePromo}
        errors={promoErrors}
        onClearError={clearPromoError}
      />

      {/* Minimum order */}
      <FormField
        label={t(keys.discounts.minOrderLabel)}
        name="minOrderAmount"
        error={fieldErrors.minOrderAmount}
        helperText={
          fieldErrors.minOrderAmount
            ? undefined
            : t(keys.discounts.minOrderHint)
        }
      >
        <div className="relative">
          <TextInput
            name="minOrderAmount"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            value={minOrderAmount}
            placeholder={t(keys.discounts.minOrderPlaceholder)}
            hasError={Boolean(fieldErrors.minOrderAmount)}
            onChange={(event) => {
              setMinOrderAmount(event.target.value);
              if (fieldErrors.minOrderAmount) {
                setFieldErrors((prev) => ({
                  ...prev,
                  minOrderAmount: undefined,
                }));
              }
            }}
            className="pe-10"
          />
          <span
            className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-[12px] font-semibold text-[color:var(--text-muted)]"
            aria-hidden="true"
          >
            EGP
          </span>
        </div>
      </FormField>

      {/* Schedule: start + expiry */}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t(keys.discounts.startsAtLabel)}
          name="startsAt"
          error={fieldErrors.schedule}
          helperText={
            fieldErrors.schedule ? undefined : t(keys.discounts.startsAtHint)
          }
        >
          <TextInput
            name="startsAt"
            type="datetime-local"
            value={startsAt}
            hasError={Boolean(fieldErrors.schedule)}
            onChange={(event) => {
              setStartsAt(event.target.value);
              if (fieldErrors.schedule) {
                setFieldErrors((prev) => ({ ...prev, schedule: undefined }));
              }
            }}
          />
        </FormField>
        <FormField
          label={t(keys.discounts.expiryLabel)}
          name="expiresAt"
          helperText={t(keys.discounts.expiryHint)}
        >
          <TextInput
            name="expiresAt"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => {
              setExpiresAt(event.target.value);
              if (fieldErrors.schedule) {
                setFieldErrors((prev) => ({ ...prev, schedule: undefined }));
              }
            }}
          />
        </FormField>
      </div>

      {/* Combinable toggle */}
      <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.discounts.combinableLabel)}
          </span>
          <ToggleInput
            name="combinable"
            checked={combinable}
            onChange={(event) => setCombinable(event.target.checked)}
            ariaLabel={t(keys.discounts.combinableLabel)}
          />
        </div>
        <p className="mt-1 text-[12px] leading-snug text-[color:var(--text-muted)]">
          {t(keys.discounts.combinableHint)}
        </p>
      </div>

      {/* Active toggle */}
      <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.discounts.activeLabel)}
          </span>
          <ToggleInput
            name="active"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            ariaLabel={t(keys.discounts.activeLabel)}
          />
        </div>
        <p className="mt-1 text-[12px] leading-snug text-[color:var(--text-muted)]">
          {t(keys.discounts.activeHint)}
        </p>
      </div>

      {/* Usage stats (edit mode only) */}
      {mode === "edit" && discount ? (
        <div className="flex items-center justify-between rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3">
          <span className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[color:var(--text-muted)]">
            {t(keys.discounts.usageStatLabel)}
          </span>
          <span className="font-mono text-[13px] font-semibold text-[color:var(--text-primary)]">
            {discount.usesCount}
          </span>
        </div>
      ) : null}

      {/* Inline action buttons are only rendered when no external formId is
          supplied. When a Modal wraps the form, the Modal footer hosts the
          Cancel/Save buttons and submits via `form="<id>"`. */}
      {formId ? null : (
        <div className="mt-1">
          <FormActionButtons
            cancelLabel={t(keys.common.cancel)}
            saveLabel={
              mode === "create"
                ? t(keys.discounts.createAction)
                : t(keys.common.save)
            }
            loadingLabel={t(keys.common.loading)}
            onCancel={onCancel}
            isSaving={isSubmitting}
          />
        </div>
      )}
    </form>
  );
}

function TruckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function PercentIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
